import type { Database } from 'sql.js';
import { queryAll, queryOne, type SqlJsBind } from '@core/infrastructure/driven/sqljs/sqljsSqlitePrimitives.ts';
import type { PuntosEntregaListPage, PuntosEntregaListQuery } from '../../../domain/puntoEntregaModel.ts';
import { mapPuntoEntregaListRow } from './mappers.ts';
import {
  PUNTO_ENTREGA_BARRIO_NOMBRE_EXPR,
  PUNTO_ENTREGA_LOCATION_FROM_SQL,
  PUNTO_ENTREGA_LUGAR_NOMBRE_EXPR,
} from './puntoEntregaLocationFromSql.ts';

type SqlBind = SqlJsBind;

export function listPuntosEntregaPage(db: Database, q: PuntosEntregaListQuery): PuntosEntregaListPage {
  const pageSize = Math.min(100, Math.max(1, Math.floor(Number(q.pageSize) || 10)));
  const page = Math.max(1, Math.floor(Number(q.page) || 1));
  const offset = (page - 1) * pageSize;

  const rIn0 = q.entregaFechaRangoInicio?.trim() ?? '';
  const rFin0 = q.entregaFechaRangoFin?.trim() ?? '';
  const rangoValido =
    rIn0.length === 10 &&
    rFin0.length === 10 &&
    /^\d{4}-\d{2}-\d{2}$/.test(rIn0) &&
    /^\d{4}-\d{2}-\d{2}$/.test(rFin0) &&
    rIn0 <= rFin0;
  const searchRaw = q.search?.trim() ?? '';
  const likePat = searchRaw ? `%${searchRaw.replaceAll(/[%_\\]/g, '')}%` : '';

  const baseFrom = `
      ${PUNTO_ENTREGA_LOCATION_FROM_SQL}
      JOIN entregas e ON e.id = pe.entrega_id`;

  const whereParts: string[] = [];
  const params: SqlBind[] = [];

  if (rangoValido) {
    whereParts.push('date(pe.fecha_hora) >= ? AND date(pe.fecha_hora) <= ?');
    params.push(rIn0, rFin0);
  }

  if (likePat) {
    whereParts.push(
      `(e.nombre LIKE ? OR ${PUNTO_ENTREGA_LUGAR_NOMBRE_EXPR} LIKE ? OR c.nombre LIKE ? OR ${PUNTO_ENTREGA_BARRIO_NOMBRE_EXPR} LIKE ?
         OR pe.fecha_hora LIKE ? OR pe.public_id LIKE ?
         OR CAST(pe.id AS TEXT) LIKE ? OR (CASE WHEN pe.es_remanente = 1 THEN 'sí remanente' ELSE 'no remanente' END) LIKE ?
         OR pe.direccion LIKE ?)`,
    );
    for (let i = 0; i < 9; i += 1) params.push(likePat);
  }

  const whereSql = whereParts.length ? `WHERE ${whereParts.join(' AND ')}` : '';

  const countRow = queryOne(db, `SELECT COUNT(*) AS n ${baseFrom} ${whereSql}`, params);
  const total = Math.max(0, Math.floor(Number(countRow?.n ?? 0)));

  const listSql = `SELECT pe.id, pe.public_id, pe.entrega_id, pe.fecha_hora, pe.es_remanente,
              e.nombre AS entrega_nombre, ${PUNTO_ENTREGA_LUGAR_NOMBRE_EXPR} AS lugar_nombre,
              c.nombre AS comuna_nombre, ${PUNTO_ENTREGA_BARRIO_NOMBRE_EXPR} AS barrio_nombre,
              (SELECT COUNT(*) FROM punto_entrega_participante pep WHERE pep.punto_entrega_id = pe.id) AS total_participantes,
              (SELECT COUNT(*) FROM punto_entrega_participante pep2 WHERE pep2.punto_entrega_id = pe.id AND pep2.asistencia = 1) AS total_capacitados
       ${baseFrom}
       ${whereSql}
       ORDER BY pe.id DESC
       LIMIT ? OFFSET ?`;
  const rows = queryAll(db, listSql, [...params, pageSize, offset]);

  return {
    total,
    items: rows.map((r) => mapPuntoEntregaListRow(r)),
  };
}
